import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { useSession } from "@/lib/auth-client";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  BarChart3,
  Bell,
  BookOpen,
  ChevronRight,
  Settings,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/more")({
  component: MorePage,
});

const mainLinks = [
  {
    to: "/results",
    icon: BarChart3,
    label: "Results",
    description: "Academic performance and marks",
  },
  {
    to: "/courses",
    icon: BookOpen,
    label: "Courses",
    description: "Enrolled subjects and credits",
  },
  {
    to: "/myexam",
    icon: BookOpen,
    label: "Exams",
    description: "Datesheet and seating plan",
  },
  {
    to: "/reminders",
    icon: Bell,
    label: "Reminders",
    description: "Upcoming tasks and deadlines",
  },
] as const;

const secondaryLinks = [
  {
    to: "/common-breaks",
    icon: Users,
    label: "Common Breaks",
    description: "Shared free time with peers",
  },
] as const;

const settingsLink = {
  to: "/settings",
  icon: Settings,
  label: "Settings",
  description: "App preferences and account",
} as const;

function MorePage() {
  const { data: session } = useSession();

  return (
    <div className="space-y-6 sm:space-y-8 max-w-2xl mx-auto pb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold sm:font-bold tracking-tight">
          More
        </h1>
        <p className="text-muted-foreground mt-1">
          Access your additional academic resources and preferences.
        </p>
      </div>

      {session?.user && (
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
                {session.user.name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2) ?? "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-base font-semibold">
                {session.user.name}
              </p>
              <p className="truncate text-sm text-muted-foreground">
                {session.user.email}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        <div className="grid gap-3">
          {mainLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block group outline-none"
            >
              <Card className="transition-all duration-200 group-hover:bg-muted/40 active:scale-[0.99] group-focus-visible:ring-2 group-focus-visible:ring-ring">
                <CardContent className="flex items-center p-4">
                  <div className="bg-primary/10 text-primary p-2.5 rounded-lg mr-4 group-hover:bg-primary/20 transition-colors">
                    <link.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">
                      {link.label}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {link.description}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3 px-1 uppercase tracking-wider">
            Other Services
          </h2>
          <div className="grid gap-3">
            {secondaryLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block group outline-none"
              >
                <Card className="transition-all duration-200 group-hover:bg-muted/40 active:scale-[0.99] group-focus-visible:ring-2 group-focus-visible:ring-ring border-muted/60 shadow-none">
                  <CardContent className="flex items-center p-3 sm:p-4">
                    <div className="text-muted-foreground p-2 rounded-lg mr-4 bg-muted/30 group-hover:bg-muted transition-colors">
                      <link.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm sm:text-base font-medium text-foreground/90">
                        {link.label}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {link.description}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-foreground/70 transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <Link to={settingsLink.to} className="block group outline-none">
            <Card className="transition-all duration-200 group-hover:bg-muted/40 active:scale-[0.99] group-focus-visible:ring-2 group-focus-visible:ring-ring">
              <CardContent className="flex items-center p-4">
                <div className="bg-muted text-foreground p-2.5 rounded-lg mr-4 transition-colors">
                  <settingsLink.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">
                    {settingsLink.label}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {settingsLink.description}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
